import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Image, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../store/slices/auth";
import {
  FiUser,
  FiPhone,
  FiMail,
  FiUsers,
  FiBook,
  FiMapPin,
  FiCalendar,
  FiHome,
  FiFlag,
  FiHash,
  FiLayers,
} from "react-icons/fi";
import { toast } from "react-hot-toast";

export default function StudentProfile() {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const activeStudent = useSelector(
    (state) => state.activestudent?.student
  );

  useEffect(() => {
    setLoading(true);
    dispatch(getProfile())
      .unwrap()
      .then((res) => setProfile(res?.data || null))
      .catch(() => toast.error("Failed to fetch profile details"))
      .finally(() => setLoading(false));
  }, [dispatch]);



  if (loading) {
    return (
      <Container className="p-5 d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "50vh" }}>
        <div className="sp-loading-ring mb-3" />
        <div className="text-muted fw-semibold">Loading profile details...</div>
      </Container>
    );
  }

  if (!profile || !activeStudent) {
    return (
      <Container className="py-5">
        <Card className="sp-empty-card sp-fade-up text-center py-5">
          <Card.Body>
            <div className="sp-stat-icon mx-auto mb-3" style={{ width: 56, height: 56, fontSize: 26 }}>
              <FiUser />
            </div>
            <div className="fw-bold fs-5 mb-1">No Student Selected</div>
            <div className="text-muted">Please select a student from header</div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const { parentDetails } = profile;

  return (
    <>
      <Container className="pb-4">
        <Card className="sp-cover-card sp-fade-up mb-4 border-0 ">
          <div className="sp-cover-banner" />

          <Card.Body style={{ marginTop: "-70px" }}>
            <Row className="align-items-center">
              <Col md={2} className="text-center position-relative">
                <div className="sp-avatar-ring position-relative d-inline-flex">
                  <Image src={activeStudent.image} roundedCircle className="sp-avatar-img" />
                  <span className="sp-status-dot" />
                </div>
              </Col>

              <Col md={10}>
                <h4 className="sp-name mb-1">{activeStudent.name}</h4>
                <div className="text-muted mb-3">
                  {activeStudent.standardId?.name} –{" "}
                  {activeStudent.sectionId?.name}
                </div>

                <Row className="g-2">
                  <Col xs={6} sm={4} md={3}>
                    <div className="sp-stat-chip d-flex align-items-center gap-2">
                      <div className="sp-stat-icon"><FiHash /></div>
                      <div>
                        <div className="sp-detail-label mb-0">Roll No</div>
                        <div className="sp-detail-value">{activeStudent.rollNo}</div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={6} sm={4} md={3}>
                    <div className="sp-stat-chip d-flex align-items-center gap-2">
                      <div className="sp-stat-icon"><FiLayers /></div>
                      <div>
                        <div className="sp-detail-label mb-0">Session</div>
                        <div className="sp-detail-value">{activeStudent.session}</div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={6} sm={4} md={3}>
                    <div className="sp-stat-chip d-flex align-items-center gap-2">
                      <div className="sp-stat-icon"><FiCalendar /></div>
                      <div>
                        <div className="sp-detail-label mb-0">DOB</div>
                        <div className="sp-detail-value">
                          {new Date(activeStudent.dob).toLocaleDateString("en-GB")}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="sp-info-card sp-fade-up mb-4 border-0 " style={{ animationDelay: "0.1s" }}>
          <div className="sp-info-header">
            <div className="sp-info-icon-badge"><FiUser /></div>
            Student Details
            <Badge className="sp-active-badge ms-auto">Active</Badge>
          </div>

          <Card.Body>
            <Row className="g-3">
              <Col xs={12} sm={6} md={4}>
                <div className="sp-detail-tile">
                  <div className="sp-detail-icon"><FiBook /></div>
                  <div>
                    <div className="sp-detail-label">Class</div>
                    <div className="sp-detail-value">
                      {activeStudent.standardId?.name} - {activeStudent.sectionId?.name}
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} md={4}>
                <div className="sp-detail-tile">
                  <div className="sp-detail-icon"><FiUsers /></div>
                  <div>
                    <div className="sp-detail-label">Gender</div>
                    <div className="sp-detail-value">{activeStudent.gender}</div>
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} md={4}>
                <div className="sp-detail-tile">
                  <div className="sp-detail-icon"><FiFlag /></div>
                  <div>
                    <div className="sp-detail-label">Nationality</div>
                    <div className="sp-detail-value">{activeStudent.nationality}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="sp-info-card sp-fade-up mb-4 border-0 " style={{ animationDelay: "0.2s" }}>
          <div className="sp-info-header">
            <div className="sp-info-icon-badge"><FiUsers /></div>
            Parent Details
          </div>

          <Card.Body>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <div className="sp-parent-tile">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="sp-parent-avatar"><FiUser /></div>
                    <div>
                      <div className="sp-detail-label">Father</div>
                      <div className="sp-detail-value fs-6">{parentDetails?.fatherName}</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="sp-detail-icon"><FiPhone /></div>
                    <div className="sp-detail-value">{parentDetails?.fatherMobileNo}</div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <div className="sp-detail-icon"><FiMail /></div>
                    <div className="sp-detail-value">{parentDetails?.fatherEmail}</div>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="sp-parent-tile">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="sp-parent-avatar"><FiUser /></div>
                    <div>
                      <div className="sp-detail-label">Mother</div>
                      <div className="sp-detail-value fs-6">{parentDetails?.motherName}</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="sp-detail-icon"><FiPhone /></div>
                    <div className="sp-detail-value">{parentDetails?.motherMobileNo}</div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <div className="sp-detail-icon"><FiMail /></div>
                    <div className="sp-detail-value">{parentDetails?.motherEmail}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="sp-info-card sp-fade-up border-0 " style={{ animationDelay: "0.3s" }}>
          <div className="sp-info-header">
            <div className="sp-info-icon-badge"><FiMapPin /></div>
            Address Details
          </div>

          <Card.Body>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <div className="sp-detail-tile">
                  <div className="sp-detail-icon"><FiMapPin /></div>
                  <div>
                    <div className="sp-detail-label">Present Address</div>
                    <div className="sp-detail-value">{activeStudent.presentAddress}</div>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="sp-detail-tile">
                  <div className="sp-detail-icon"><FiHome /></div>
                  <div>
                    <div className="sp-detail-label">Permanent Address</div>
                    <div className="sp-detail-value">{activeStudent.permanentAddress}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}